import { useEffect, useState } from "react";
import { useNavigate, Link } from "react-router-dom";
import axios from "axios";
import { motion } from "framer-motion";
import { ArrowLeft, LogOut, CreditCard, Loader2, ArrowUpRight } from "lucide-react";
import { Logo, LoadingMark } from "@/components/Logo";
import { useAuth } from "@/context/AuthContext";
import { spring, useUITheme, ThemeToggle, pageTheme } from "@/components/ios";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function Account() {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const [plan, setPlan] = useState(undefined);
  const { dark, toggle } = useUITheme();
  const t = pageTheme(dark);

  useEffect(() => {
    if (user === false) navigate("/login");
  }, [user, navigate]);

  useEffect(() => {
    if (!user) return;
    const token = localStorage.getItem("doclyx_token");
    axios
      .get(`${API}/account/subscription`, { headers: { Authorization: `Bearer ${token}` }, withCredentials: true })
      .then((res) => setPlan(res.data.plan))
      .catch(() => setPlan(null));
  }, [user]);

  if (!user) {
    return (
      <main className={`${dark ? "v4-theme bg-[#050505]" : "bg-[#FDFDFB]"} min-h-screen flex items-center justify-center`}>
        <LoadingMark label="Loading account" />
      </main>
    );
  }

  const handleLogout = async () => {
    await logout();
    navigate("/");
  };

  return (
    <main className={`min-h-screen font-sans ${t.page}`} data-testid="account-page" data-theme={dark ? "dark" : "light"}>
      <header className={`sticky top-0 z-40 ${dark ? "bg-black/60 border-white/[0.08]" : "bg-white/70 border-black/[0.05]"} backdrop-blur-2xl backdrop-saturate-150 border-b`}>
        <div className="max-w-2xl mx-auto px-5 h-16 flex items-center justify-between">
          <Link to="/" data-testid="account-home-link"><Logo size={26} /></Link>
          <div className="flex items-center gap-3">
            <ThemeToggle dark={dark} onToggle={toggle} testid="account-theme-toggle" />
            <button
              onClick={handleLogout}
              data-testid="logout-button"
              className={`flex items-center gap-2 text-[14px] font-medium ${t.linkMuted} transition-colors duration-200 min-h-[44px]`}
            >
              <LogOut className="w-4 h-4" strokeWidth={1.5} /> Sign out
            </button>
          </div>
        </div>
      </header>

      <div className="max-w-2xl mx-auto px-5 py-10 md:py-14">
        <motion.div initial={{ opacity: 0, y: 16 }} animate={{ opacity: 1, y: 0 }} transition={spring}>
          <h1 className={`font-bold tracking-tight text-3xl mb-1.5 ${t.primary}`} data-testid="account-greeting">
            {user.name ? `Hello, ${user.name}.` : "Hello."}
          </h1>
          <p className={`text-[15px] ${t.secondary} mb-8`} data-testid="account-email">{user.email}</p>

          <div className={`rounded-sm p-6 md:p-8 mb-4 ${t.card}`} data-testid="account-plan-card">
            <span className={`text-[13px] font-medium ${t.secondary} block mb-4`}>Subscription</span>
            {plan === undefined ? (
              <Loader2 className={`w-5 h-5 ${t.tertiary} animate-spin`} strokeWidth={1.5} />
            ) : plan ? (
              <div className="flex items-end justify-between flex-wrap gap-4">
                <div>
                  <p className={`text-2xl font-bold tracking-tight ${t.primary}`} data-testid="account-plan-name">{plan.name}</p>
                  <p className={`text-[14px] ${t.secondary} mt-1`} data-testid="account-plan-detail">
                    ${plan.amount}/{plan.interval} · <span className={`${t.link} font-medium capitalize`}>{plan.status}</span>
                  </p>
                </div>
                <div className="flex items-center gap-3 flex-wrap">
                  <Link to="/workspace" data-testid="open-workspace-button"
                    className={`flex items-center gap-2 min-h-[44px] px-5 rounded-full ${t.primaryBtn} text-[14px] font-semibold active:scale-95 transition-transform duration-150`}>
                    Open workspace
                  </Link>
                  <Link to="/billing" data-testid="manage-subscription-link"
                    className={`flex items-center gap-2 min-h-[44px] px-5 rounded-full ${t.secondaryBtn} text-[14px] font-semibold active:scale-95 transition-transform duration-150`}>
                    <CreditCard className="w-4 h-4" strokeWidth={1.5} /> Manage
                  </Link>
                </div>
              </div>
            ) : (
              <div className="flex items-end justify-between flex-wrap gap-4">
                <p className={`text-[15px] ${t.secondary}`} data-testid="account-no-plan">No active subscription on this email.</p>
                <Link to="/#pricing" data-testid="view-plans-link"
                  className={`flex items-center gap-2 min-h-[44px] px-5 rounded-full ${t.primaryBtn} text-[14px] font-semibold active:scale-95 transition-transform duration-150`}>
                  View plans
                </Link>
              </div>
            )}
          </div>

          {user.role === "admin" && (
            <Link to="/admin" data-testid="admin-wingman-link"
              className="block rounded-sm bg-[#0A0A0A] text-white p-6 md:p-8 mb-4 border border-white/[0.08] active:scale-[0.99] transition-transform duration-150">
              <span className="text-[11px] font-semibold uppercase tracking-[0.15em] text-white/50 block mb-2">Admin</span>
              <p className="text-xl font-semibold tracking-tight flex items-center gap-2">
                Command center <ArrowUpRight className="w-4 h-4 text-white/60" strokeWidth={2} />
              </p>
              <p className="text-[14px] text-white/60 mt-1">Revenue, subscriptions, tool health — plus the Wingman marketing studio.</p>
            </Link>
          )}

          <Link to="/" data-testid="account-back-link"
            className={`inline-flex items-center gap-2 text-[14px] ${t.linkMuted} transition-colors duration-200 mt-4 min-h-[44px]`}>
            <ArrowLeft className="w-4 h-4" strokeWidth={1.5} /> Back to Doclyx
          </Link>
        </motion.div>
      </div>
    </main>
  );
}
